import { useState, useEffect, useMemo } from "react";
import { useAdminLanguage } from "../../layouts/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Search, CreditCard, Ban, Trash2, Gift, ArrowUpDown, ChevronUp, ChevronDown, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { exportToCSV } from "../../utils/exportCSV";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [giftDialog, setGiftDialog] = useState(null);
  const [giftAmount, setGiftAmount] = useState(5);
  const { t } = useAdminLanguage();

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/api/admin/users`, { credentials: 'include' });
      if (res.ok) setUsers(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDir === "asc" ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />;
  };

  const sorted = useMemo(() => {
    let filtered = users.filter(u =>
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
    );
    return filtered.sort((a, b) => {
      let va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [users, search, sortKey, sortDir]);

  const handleSuspend = async (userId, isSuspended) => {
    const action = isSuspended ? "activate" : "suspend";
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}/suspend?action=${action}`, { method: "POST", credentials: "include" });
      if (res.ok) { toast.success(`Utilisateur ${action === "suspend" ? "suspendu" : "reactive"}`); fetchUsers(); }
    } catch (e) { toast.error(t("connection_error")); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t("confirm_delete") + " " + t("warning_delete"))) return;
    try {
      const res = await fetch(`${API}/api/admin/users/${userId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success(t("user_deleted")); fetchUsers(); }
    } catch (e) { toast.error(t("connection_error")); }
  };

  const handleGiftCredits = async () => {
    if (!giftDialog || giftAmount <= 0) return;
    try {
      const res = await fetch(`${API}/api/admin/users/${giftDialog}/credits`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credits: giftAmount })
      });
      if (res.ok) { toast.success(`${giftAmount} credits offerts !`); setGiftDialog(null); setGiftAmount(5); fetchUsers(); }
      else toast.error(t("error_generic"));
    } catch (e) { toast.error(t("connection_error")); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">{t("users")}</h1>
          <p className="text-zinc-500 text-sm">Gerez les comptes entreprises et recruteurs.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white" data-testid="export-users-csv" onClick={() => exportToCSV(sorted, [
            { label: "Nom", key: "name" },
            { label: "Email", key: "email" },
            { label: "Credits", key: "interview_credits" },
            { label: "Statut", accessor: r => r.is_suspended ? "Suspendu" : "Actif" },
            { label: "Date inscription", key: "created_at" }
          ], "ema_utilisateurs")}>
            <Download className="w-4 h-4 mr-2" />CSV
          </Button>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input className="bg-zinc-900 border-zinc-800 pl-10 text-white" placeholder={t("search")} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("name")}>
                  <span className="flex items-center">{t("name")}<SortIcon col="name" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("email")}>
                  <span className="flex items-center">{t("email")}<SortIcon col="email" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("interview_credits")}>
                  <span className="flex items-center">{t("credits")}<SortIcon col="interview_credits" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("is_suspended")}>
                  <span className="flex items-center">{t("status")}<SortIcon col="is_suspended" /></span>
                </TableHead>
                <TableHead className="text-zinc-400 cursor-pointer select-none" onClick={() => handleSort("created_at")}>
                  <span className="flex items-center">{t("date")}<SortIcon col="created_at" /></span>
                </TableHead>
                <TableHead className="text-right text-zinc-400">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(user => (
                <TableRow key={user.user_id} className="border-zinc-800 hover:bg-zinc-800/50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-indigo-500/20 rounded-full flex items-center justify-center text-indigo-400 font-bold text-xs">{user.name?.[0]}</div>
                      <span className="text-white text-sm font-medium">{user.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-zinc-400 text-xs">{user.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-zinc-300 text-sm">
                      <CreditCard className="w-3.5 h-3.5 text-zinc-500" />{user.interview_credits}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={user.is_suspended ? "bg-red-500/10 text-red-400 border-red-500/20" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"}>
                      {user.is_suspended ? t("suspended") : t("active")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-zinc-500 text-xs">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={`gift-btn-${user.user_id}`} variant="ghost" size="icon" className="h-7 w-7 text-amber-400 hover:text-amber-300" onClick={() => setGiftDialog(user.user_id)} title={t("gift_credits")}>
                        <Gift className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-orange-400" onClick={() => handleSuspend(user.user_id, user.is_suspended)} title={user.is_suspended ? t("reactivate") : t("suspend")}>
                        <Ban className="w-3.5 h-3.5" />
                      </Button>
                      <Button data-testid={`delete-user-${user.user_id}`} variant="ghost" size="icon" className="h-7 w-7 text-zinc-400 hover:text-red-400" onClick={() => handleDelete(user.user_id)} title={t("delete")}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Gift Credits Dialog */}
      <Dialog open={!!giftDialog} onOpenChange={() => setGiftDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white sm:max-w-sm">
          <DialogHeader><DialogTitle>{t("gift_credits")}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Nombre de credits</Label>
              <Input type="number" min="1" max="1000" value={giftAmount} onChange={e => setGiftAmount(parseInt(e.target.value) || 0)} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setGiftDialog(null)}>{t("cancel")}</Button>
              <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleGiftCredits}>
                <Gift className="w-4 h-4 mr-2" />Offrir
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
